import { TailwindColor } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'

import { TransportTypeEnum, transportTypeMapping } from '../../enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../trip/properties.trip'
import { TripStepProperties } from './properties.trip-step'
import { TripBookingTypes } from './trip-booking'
import { TripStepTypes } from './trip-step'

type DirectionalityDetails = {
  label: string
  bgColor: TailwindColor
  textColor: TailwindColor
  type: TransportTypeEnum
}

export type SingleDirectionalityDetails = Omit<DirectionalityDetails, 'type'> & {
  type: TransportTypeEnum
}

export type TripBookingLabelDetailsOptions = {
  count?: ('reserved-allocated' | 'allocated-dispatched' | 'reserved-dispatched')[]

  // if true, the label will be display the cost of the trip
  showCost?: boolean

  displayLabel?: boolean

  // if true, the label will be displayed with the travel companion
  displayTravelCompany?: boolean
  displayDate?: boolean
  displayArrivalTime?: boolean
}

export type TripBookingLabelOptions = {
  displayDate: boolean
}

const tripBookingLabelDefaultOptions: TripBookingLabelOptions = {
  displayDate: false
}

export const tripBookingLabelDetailsDefaultOptions: TripBookingLabelDetailsOptions = {
  count: [],
  showCost: true,
  displayTravelCompany: false,
  displayArrivalTime: false
}

export class TripBookingProperties {
  static sortTripStepsById<T extends { id: number }>(tripSteps?: T[]): T[] | undefined {
    if (!tripSteps) return undefined

    return [...tripSteps].sort((a, b) => a.id - b.id)
  }

  static getLabel(
    tripBooking: TripBookingTypes.Light,
    options: TripBookingLabelOptions = tripBookingLabelDefaultOptions
  ): string {
    const label: string[] = []

    // get the HH:MM of the trip
    const tripHHMM = this.getTripDepartureHHMM(tripBooking)
    const tripDates =
      this.sortTripStepsById(tripBooking.tripSteps)
        ?.map((tripStep) => tripStep.departureDate)
        .filter(Boolean) ?? []
    const uniqueTripDates = [...new Set(tripDates)]

    label.push(transportTypeMapping[tripBooking.directionality]?.label)

    if (options.displayDate && uniqueTripDates.length > 0) {
      const formattedDates = uniqueTripDates.map((date) => DateFormatter.DDMMYYYY(date))
      label.push(
        formattedDates.length === 1
          ? formattedDates[0]
          : `${formattedDates[0]} au ${formattedDates[formattedDates.length - 1]}`
      )
    }

    if (!tripBooking.isTakeInCharge) {
      label.push(tripBooking.reservationNumber)

      // if we have a tripHHMM, add it to the label since sometimes it's undefined because the trip booking has no inbound or outbound trip step
      if (tripHHMM) label.push(tripHHMM)
      label.push(`${tripBooking.totalPrice}€`)
    } else {
      label.push(tripBooking.label)
      label.push(DateFormatter.DDMMYYYY(tripBooking.date))
    }

    return label.filter(Boolean).join(' - ')
  }

  /**
   * Get the trip steps according to the directionality of the trip booking
   * @param tripBooking
   * @returns trip steps
   */
  static getTripStepsByDirectionality(
    directionality: TripStepTypeEnum,
    tripBooking?: TripBookingTypes.Basic
  ): TripStepTypes.Light[] | undefined {
    if (!tripBooking || !tripBooking.tripSteps) return undefined

    return this.sortTripStepsById(
      tripBooking.tripSteps.filter((tripStep) => tripStep.directionality === directionality)
    )
  }

  /**
   * Return all the trip steps of a tripBooking
   * @param tripBooking - The tripBooking to get the trip steps from
   * @returns The trip steps of the tripBooking
   */
  static getAllTripSteps<T extends TripBookingTypes.Basic>(
    tripBooking?: T,
    directionality?: TripStepTypeEnum
  ): TripStepTypes.Light[] | undefined {
    if (!tripBooking) return undefined

    const { tripSteps } = tripBooking

    if (!tripSteps) return undefined

    const sortedInbounds =
      this.getTripStepsByDirectionality(TripStepTypeEnum.INBOUND, tripBooking) ?? []

    const sortedOutbounds =
      this.getTripStepsByDirectionality(TripStepTypeEnum.OUTBOUND, tripBooking) ?? []

    // Return trip steps based on directionality parameter if provided
    if (directionality) {
      // For one-way trips, return inbound steps if they exist
      if (directionality === TripStepTypeEnum.INBOUND)
        return sortedInbounds.length > 0 ? sortedInbounds : undefined
      // For return trips, return outbound steps if they exist
      return sortedOutbounds.length > 0 ? sortedOutbounds : undefined
    }

    // If no directionality parameter, use trip booking's directionality
    if (tripBooking.directionality === TransportTypeEnum.ONE_WAY)
      return sortedInbounds.length > 0 ? sortedInbounds : undefined
    return sortedOutbounds.length > 0 ? sortedOutbounds : undefined
  }

  /**
   * Return the trip step of a trip booking depending on the directionality
   * @param tripBooking - The trip booking to get the trip step from
   * @param directionality - The directionality to filter by (INBOUND or OUTBOUND)
   * @returns trip step
   */
  static getTripStep<T extends TripBookingTypes.Basic>(
    tripBooking?: T,
    directionality?: TripStepTypeEnum
  ): TripStepTypes.Light | undefined {
    if (!tripBooking) return undefined

    const tripSteps = this.getAllTripSteps(tripBooking, directionality)

    if (!tripSteps) return undefined

    // return the first trip step
    if (tripBooking.directionality === TransportTypeEnum.ONE_WAY) return tripSteps[0]

    // return the last trip step
    return tripSteps[tripSteps.length - 1]
  }

  /**
   * Return the HH:MM of a trip step depending on the directionality, if the directionality is one way, return the HH:MM of the first trip step,
   * if the directionality is return, return the HH:MM of the last trip step
   * @param tripBooking - The tripBooking to get the HH:MM from
   * @returns HH:MM
   */
  static getTripDepartureHHMM(
    tripBooking?: TripBookingTypes.Basic,
    directionality?: TripStepTypeEnum
  ): string | undefined {
    const tripStep = this.getTripStep(tripBooking, directionality)
    if (!tripStep) return undefined

    return TripStepProperties.getDepartureDateTime(tripStep)
  }

  /**
   * Return the HH:MM of a trip step depending on the directionality, if the directionality is one way, return the HH:MM of the first trip step,
   * if the directionality is return, return the HH:MM of the last trip step
   * @param tripBooking - The tripBooking to get the HH:MM from
   * @returns HH:MM
   */
  static getTripArrivalHHMM(
    tripBooking?: TripBookingTypes.Basic,
    directionality?: TripStepTypeEnum
  ): string | undefined {
    const tripStep = this.getTripStep(tripBooking, directionality)
    if (!tripStep) return undefined

    return TripStepProperties.getArrivalDateTime(tripStep)
  }

  /**
   * get the directionality info for a tripBooking
   * @param tripBooking - The tripBooking to get the directionality info from
   * @returns `DirectionalityDetails`
   */
  static getDirectionalityDetails(
    direction?: TransportTypeEnum
  ): SingleDirectionalityDetails | undefined {
    if (!direction) return undefined

    const { label, bgColor, textColor } = transportTypeMapping[direction]
    return { label, bgColor, textColor, type: direction }
  }

  /**
   * Sort the trip bookings by time
   * @param tripBookings - The trip bookings to sort
   * @returns The sorted trip bookings
   */
  static sortTripBookingsByTime<T extends TripBookingTypes.Basic>(tripBookings: T[]) {
    return tripBookings.sort((a, b) => {
      const aHHMM = TripBookingProperties.getTripDepartureHHMM(a)
      const bHHMM = TripBookingProperties.getTripDepartureHHMM(b)
      if (!aHHMM || !bHHMM) return 0

      return aHHMM.localeCompare(bHHMM)
    })
  }

  /**
   * get the details of a tripBooking
   * @param tripBooking - The tripBooking to get the details from
   * @param options - The options to get the details from
   * @returns `string`
   */
  static getDetails(
    tripBooking?: TripBookingTypes.Light,
    options: TripBookingLabelDetailsOptions = tripBookingLabelDetailsDefaultOptions,
    directionality?: TripStepTypeEnum
  ): string | undefined {
    if (!tripBooking) return undefined

    const { count, showCost = true, displayTravelCompany: displayTravelCompanion } = options
    const details: string[] = []

    if (tripBooking.isTakeInCharge && tripBooking.meetingTimeForParents) {
      details.push('Prise en charge')
      details.push(tripBooking.label)

      const date = DateFormatter.createDate(tripBooking.meetingTimeForParents)
      if (options.displayDate) details.push(DateFormatter.DDMMYYYY(date))
      details.push(DateFormatter.HHhMM(date))
    } else {
      details.push(tripBooking.reservationNumber)
      const tripStep = this.getTripStep(tripBooking)
      if (tripStep && options.displayDate)
        details.push(DateFormatter.DDMMYYYY(tripStep.departureDate))

      // get the HH:MM of the trip and add it to the main details if it exists
      const tripDepartureTime = TripBookingProperties.getTripDepartureHHMM(
        tripBooking,
        directionality
      )
      if (tripDepartureTime) details.push(tripDepartureTime)

      const tripArrivalTime = TripBookingProperties.getTripArrivalHHMM(tripBooking, directionality)
      if (tripArrivalTime) details.push(tripArrivalTime)

      if (showCost) details.push(`${tripBooking.totalPrice}€`)

      details.push(TripProperties.getTripLabel(tripBooking.relatedTrip))

      if (displayTravelCompanion) {
        details.push(`Compagnie : ${tripBooking.travelCompany?.name ?? '-'}`)
      }
    }

    if (count?.includes('reserved-allocated')) {
      const numberOfStudentPlacesRemaining =
        tripBooking.numberOfStudentsPlacesReserved - tripBooking.numberOfStudentsPlacesAllocated

      const numberOfStaffPlacesRemaining =
        tripBooking.numberOfStaffPlacesReserved - tripBooking.numberOfStaffPlacesAllocated
      details.push(`# ${numberOfStudentPlacesRemaining} places jeunes réservées non réparties`)
      details.push(`# ${numberOfStaffPlacesRemaining} places encadrants réservées non réparties`)
    } else if (count?.includes('allocated-dispatched')) {
      const numberOfStudentPlacesRemaining =
        tripBooking.numberOfStudentsPlacesAllocated - tripBooking.numberOfStudentsPlacesDispatched

      const numberOfStaffPlacesRemaining =
        tripBooking.numberOfStaffPlacesAllocated - tripBooking.numberOfStaffPlacesDispatched
      // more pax dispatched than allocated to the réservation: the répartition must be completed
      details.push(
        numberOfStudentPlacesRemaining < 0
          ? `# ${-numberOfStudentPlacesRemaining} jeunes dispatchés au-delà des places réparties`
          : `# ${numberOfStudentPlacesRemaining} places jeunes réparties non dispatchées restantes`
      )
      details.push(
        numberOfStaffPlacesRemaining < 0
          ? `# ${-numberOfStaffPlacesRemaining} encadrants dispatchés au-delà des places réparties`
          : `# ${numberOfStaffPlacesRemaining} places encadrants réparties non dispatchées restantes`
      )
    } else if (count?.includes('reserved-dispatched')) {
      // add the number of places dispatched from the tripBooking

      const numberOfStudentPlacesRemaining =
        tripBooking.numberOfStudentsPlacesReserved - tripBooking.numberOfStudentsPlacesDispatched

      const numberOfStaffPlacesRemaining =
        tripBooking.numberOfStaffPlacesReserved - tripBooking.numberOfStaffPlacesDispatched

      details.push(`# ${numberOfStudentPlacesRemaining} places jeunes réservées restantes`)
      details.push(`# ${numberOfStaffPlacesRemaining} places encadrants réservées restantes`)
    }

    // only in case of a roundtrip, add the
    if (tripBooking.directionality === TransportTypeEnum.ROUND_TRIP) {
      const outboundTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        TripStepTypeEnum.OUTBOUND,
        tripBooking
      )

      const inboundTripSteps = TripBookingProperties.getTripStepsByDirectionality(
        TripStepTypeEnum.INBOUND,
        tripBooking
      )

      const departureDateOutbound = outboundTripSteps?.[0]?.departureDate
      const departureDateInbound = inboundTripSteps?.[0]?.departureDate

      if (directionality === TripStepTypeEnum.OUTBOUND && departureDateInbound) {
        details.push(`(Aller le ${DateFormatter.DDMMYYYY(departureDateInbound)})`)
      }

      if (directionality === TripStepTypeEnum.INBOUND && departureDateOutbound) {
        details.push(`(Retour le ${DateFormatter.DDMMYYYY(departureDateOutbound)})`)
      }
    }

    // return the details filtered and joined by a dash
    return details.filter(Boolean).join(' - ')
  }

  /**
   * Get the dates of the trip steps
   * @param tripBooking - The tripBooking to get the dates from
   * @returns The dates of the trip steps
   */
  static getTripStepDates(tripBooking: TripBookingTypes.Basic): Date[] {
    const dates: Date[] = []

    const inbounds = TripBookingProperties.getTripStepsByDirectionality(
      TripStepTypeEnum.INBOUND,
      tripBooking
    )

    const outbounds = TripBookingProperties.getTripStepsByDirectionality(
      TripStepTypeEnum.OUTBOUND,
      tripBooking
    )

    if (
      inbounds &&
      inbounds.length > 0 &&
      (tripBooking.directionality === TransportTypeEnum.ONE_WAY ||
        tripBooking.directionality === TransportTypeEnum.ROUND_TRIP)
    )
      dates.push(new Date(inbounds[0].departureDate))

    if (
      outbounds &&
      outbounds.length > 0 &&
      (tripBooking.directionality === TransportTypeEnum.RETURN ||
        tripBooking.directionality === TransportTypeEnum.ROUND_TRIP)
    )
      dates.push(new Date(outbounds[outbounds.length - 1].arrivalDate))

    return dates
  }

  // Get trip booking place along with parent time
  static getTripBookingPlaceAlongWithParentTime(
    directionality: TripStepTypeEnum,
    tripBooking?: TripBookingTypes.Basic
  ): string {
    if (!tripBooking) return '-'

    const tripSteps = this.getTripStepsByDirectionality(directionality, tripBooking)
    const firstStep = tripSteps?.[0]
    if (!firstStep) return '-'

    switch (directionality) {
      case TripStepTypeEnum.INBOUND:
        return TripStepProperties.getDepartureTripStepPlaceAndParentTime(firstStep)
      case TripStepTypeEnum.OUTBOUND:
        return TripStepProperties.getArrivalTripStepPlaceAndParentTime(firstStep)
      default:
        return '-'
    }
  }

  static getStep = (
    reservation: TripBookingTypes.Basic,
    type: 'first' | 'last',
    key: 'departureDate' | 'arrivalDate'
  ) => {
    const orderedTripSteps = this.sortTripStepsById(reservation.tripSteps) ?? []
    const tripSteps = type === 'last' ? [...orderedTripSteps].reverse() : orderedTripSteps

    if (tripSteps.length === 0) return undefined

    return tripSteps.find((step) => !!step[key])
  }

  static getDisplayArrivalStep = (reservation: TripBookingTypes.Basic) => {
    const directionality =
      reservation.directionality === TransportTypeEnum.RETURN
        ? TripStepTypeEnum.OUTBOUND
        : TripStepTypeEnum.INBOUND

    const tripSteps = this.getTripStepsByDirectionality(directionality, reservation) ?? []
    const tripStepsWithArrival = tripSteps.filter((step) => !!step.arrivalDate)

    return tripStepsWithArrival[tripStepsWithArrival.length - 1]
  }

  static getTripStepDetails = (reservation: TripBookingTypes.Basic) => {
    return {
      departureDate: this.getStep(reservation, 'first', 'departureDate')?.departureDate,
      arrivalDate: this.getStep(reservation, 'first', 'arrivalDate')?.arrivalDate,
      departurePlace: this.getStep(reservation, 'first', 'departureDate')?.departurePlace,
      arrivalPlace:
        this.getDisplayArrivalStep(reservation)?.arrivalPlace ??
        this.getStep(reservation, 'last', 'arrivalDate')?.arrivalPlace
    }
  }

  static getDeparturePlace(tripBooking?: TripBookingTypes.Basic) {
    if (!tripBooking) return undefined
    const tripStep = this.getTripStepDetails(tripBooking)
    return tripStep?.departurePlace
  }
}
